
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { Route } from 'vue-router';
import { namespace } from 'vuex-class';

import { ContentHeaderPersonFragment } from '@/components/content-header/graphql/fragments/ContentHeaderPerson.fragment';
import {
	GetContentHeaderPersonDocument,
	GetContentHeaderPersonQueryVariables,
} from '@/components/content-header/graphql/queries/GetContentHeaderPerson.query';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

const Routing = namespace('routing');
const Language = namespace('language');

@Component({
	name: 'ContentHeaderPersonDetails',
	apollo: {
		contentHeaderPerson: {
			query: GetContentHeaderPersonDocument,
			variables(): GetContentHeaderPersonQueryVariables {
				return this.queryVariables;
			},
			update(data) {
				return data.node;
			},
			error(e) {
				captureMessageForSentry(
					'[GraphQL Get Person]:',
					{ error: e, where: 'Component: ContentHeaderPersonDetails' },
					'error'
				);
			},
		},
	},
})
export default class ContentHeaderPersonDetails extends Vue {
	expanded = false;
	contentHeaderPerson: ContentHeaderPersonFragment = {} as ContentHeaderPersonFragment;

	@Prop({ required: true }) personId: string;
	@Prop({ default: null }) personBackdrop: string | null;

	@Routing.State activeRoute: Route;

	@Language.Getter language: string;
	@Language.Getter country: string;

	get queryVariables(): GetContentHeaderPersonQueryVariables {
		return {
			nodeId: this.personId,
			language: this.language,
			country: this.country,
		};
	}

	get backdropStyle(): object {
		if (this.personBackdrop && this.$options.filters) {
			const backgroundImageUrl = this.$options.filters.Thumbor({
				url: this.personBackdrop,
				imageType: 'backdrop',
			});
			return {
				['background-image']: `url('${backgroundImageUrl}')`,
			};
		}
		return {};
	}

	get personDetails() {
		return this.contentHeaderPerson?.content ?? {};
	}

	get yearOfBirth() {
		const dateOfBirth = this.personDetails?.dateOfBirth;
		return dateOfBirth ? new Date(dateOfBirth).getFullYear() : '';
	}

	get isLoading() {
		return this.$apollo.queries.contentHeaderPerson.loading;
	}

	@Watch('activeRoute', { immediate: true })
	onActiveRouteChange() {
		this.expanded = false;
	}

	toggle() {
		this.expanded = !this.expanded;
		this.$emit('toggle');
		TrackingHelper.trackEvent('content_header', {
			action: this.expanded ? 'expanded' : 'collapsed',
		});
	}
}
